import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { Ban, Mail, Plus, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useCreateSubscriber,
  useResendConfirmation,
  useSubscribers,
  useUnsubscribeSubscriber,
} from "@/hooks/api/useSubscribers";

export function SubscribersPage() {
  const { data: subscribers } = useSubscribers();
  const create = useCreateSubscriber();
  const resend = useResendConfirmation();
  const unsubscribe = useUnsubscribeSubscriber();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", maxPrice: "", city: "" });

  const reset = () => {
    setForm({ email: "", maxPrice: "", city: "" });
    setShowForm(false);
  };

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) return;
    create.mutate(
      {
        email: form.email,
        language: "nl",
        preferences: {
          max_price: form.maxPrice ? Number(form.maxPrice) : null,
          city: form.city || null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Subscriber added — a confirmation email was sent");
          reset();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const prefSummary = (p: Record<string, unknown>) => {
    const parts: string[] = [];
    if (p.city) parts.push(String(p.city));
    if (p.max_price) parts.push(`≤ €${p.max_price}`);
    if (p.min_bedrooms) parts.push(`${p.min_bedrooms}+ bd`);
    return parts.join(" · ") || "—";
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Subscribers
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {subscribers ? `${subscribers.length} alert subscribers` : "Loading…"}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          New subscriber
        </Button>
      </header>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <form onSubmit={onCreate} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Preferred city</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxPrice">Max price (€)</Label>
                <Input
                  id="maxPrice"
                  type="number"
                  value={form.maxPrice}
                  onChange={(e) => setForm({ ...form, maxPrice: e.target.value })}
                />
              </div>
              <div className="flex gap-2 sm:col-span-3">
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? "Saving…" : "Add subscriber"}
                </Button>
                <Button type="button" variant="ghost" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {subscribers && subscribers.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Preferences</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3 font-medium">{s.email}</td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {prefSummary(s.preferences)}
                    </td>
                    <td className="px-6 py-3">
                      {s.is_unsubscribed ? (
                        <Badge tone="danger" dot>
                          Unsubscribed
                        </Badge>
                      ) : s.is_confirmed ? (
                        <Badge tone="success" dot>
                          Confirmed
                        </Badge>
                      ) : (
                        <Badge tone="warning" dot>
                          Pending
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex justify-end gap-1">
                        {!s.is_confirmed && !s.is_unsubscribed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={resend.isPending}
                            onClick={() =>
                              resend.mutate(s.id, {
                                onSuccess: () => toast.success("Confirmation re-sent"),
                                onError: () => toast.error("Could not resend"),
                              })
                            }
                          >
                            <Send className="size-4" />
                            Resend
                          </Button>
                        )}
                        {!s.is_unsubscribed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={unsubscribe.isPending}
                            onClick={() =>
                              unsubscribe.mutate(s.id, {
                                onSuccess: () => toast.success("Subscriber unsubscribed"),
                                onError: () => toast.error("Could not unsubscribe"),
                              })
                            }
                          >
                            <Ban className="size-4 text-destructive" />
                            Unsubscribe
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <Mail className="size-8 opacity-40" />
              <p className="text-sm">No subscribers yet.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
